import {Component, input, OnInit, QueryList, ViewChildren} from '@angular/core';
import {catchError, defaultIfEmpty, forkJoin, Observable, of, switchMap} from 'rxjs';
import {ContainerService} from '../../container/container.service';
import {Container} from '../../container/container';
import {Fluid} from 'primeng/fluid';
import {Button} from 'primeng/button';
import {ForecastService} from '../../forecast/forecast.service';
import {Forecast} from '../../forecast/forecast';
import {CustomField} from '../../custom-field/custom-field';
import {CustomFieldService} from '../../custom-field/custom-field.service';
import {ContainerImportDialog, ContainerImportRow} from './container-import-dialog/container-import-dialog';
import {ContainerPanel} from './container-panel/container-panel';
import {ContainerCustomFieldValue} from '../../container-custom-field-value/container-custom-field-value';
import {ContainerCustomFieldService} from '../../container-custom-field-value/container-custom-field.service';
import {map} from 'rxjs/operators';

@Component({
    selector: 'app-forecast-containers',
    imports: [Fluid, Button, ContainerImportDialog, ContainerPanel],
    templateUrl: './forecast-containers.html',
    styleUrl: './forecast-containers.scss',
})
export class ForecastContainers implements OnInit {

    @ViewChildren(ContainerPanel)
    private panels!: QueryList<ContainerPanel>;

    protected forecastId = input<string>();
    protected forecast: Forecast | undefined;
    protected containers: Container[] = [];
    protected customFields: CustomField[] = [];
    protected importDialogVisible = false;
    protected saving = false;

    protected newContainerIds = new Set<string>(); // track which are not yet persisted

    private get forecastOrThrow(): Forecast {
        if (!this.forecast) {
            throw new Error('Forecast accessed before it was loaded');
        }
        return this.forecast;
    }

    constructor(
        private forecastService: ForecastService,
        private containerService: ContainerService,
        private customFieldService: CustomFieldService,
        private containerCustomFieldService: ContainerCustomFieldService,
    ) {}

    ngOnInit(): void {
        const forecastId = this.forecastId();
        if (!forecastId) return;

        this.forecastService.getForecast(forecastId).subscribe(forecast => {
            this.forecast = forecast;
            this.customFieldService
                .loadCustomFields(forecast.planningParameter.manufacturer)
                .subscribe(collection => {
                    this.customFields = collection.getCustomFields();
                });
        });

        this.containerService.getAllContainersByForecast(forecastId).subscribe(collection => {
            this.containers = collection.getContainers();
        });
    }

    protected onImportConfirmed(rows: ContainerImportRow[]): void {
        rows
            .filter(row => !!row.containerFields.id)
            .forEach(row => {
                const {containerFields, customFieldValues} = row;
                const existingIndex = this.containers.findIndex(c => c.id === containerFields.id);

                const existingContainer = existingIndex >= 0 ? this.containers[existingIndex] : null;

                const mergedCustomValues = [
                    // keep existing values not touched by import
                    ...(existingContainer?.customValues ?? []).filter(
                        cv => !customFieldValues.find(cfv => this.customFields.find(cf => cf.id === cfv.customFieldId)?.name === cv.field)
                    ),
                    // add/override imported values
                    ...customFieldValues.map(cfv => {
                        const cf = this.customFields.find(f => f.id === cfv.customFieldId);
                        return Object.assign(new ContainerCustomFieldValue(), {
                            id: cfv.customFieldId,
                            field: cf?.name ?? cfv.customFieldId,
                            value: cfv.value,
                        });
                    }),
                ];

                const merged = Object.assign(new Container(), existingContainer ?? {}, {
                    id: containerFields.id,
                    ...Object.fromEntries(Object.entries(containerFields).filter(([, v]) => v !== undefined)),
                    customValues: mergedCustomValues,
                });

                if (existingIndex >= 0) {
                    // Replace existing entry — ngOnChanges in panel will patch the form
                    this.containers = this.containers.map((c, i) => i === existingIndex ? merged : c);
                } else {
                    // Add new entry and mark it as unpersisted
                    this.newContainerIds.add(merged.id);
                    this.containers = [...this.containers, merged];
                }
            });
    }

    protected saveAll(): void {
        this.saving = true;

        const forecastIri = ForecastService.buildIri(this.forecastOrThrow);

        const newPanels      = this.panels.filter(panel => this.newContainerIds.has(panel.getPayload().id));
        const existingPanels = this.panels.filter(panel => !this.newContainerIds.has(panel.getPayload().id));

        const createRequests$ = newPanels.map(panel => {
            const { id, payload } = panel.getPayload();
            return this.containerService.createContainer(id, { ...payload, forecast: forecastIri });
        });

        const bulkPatchPayload = existingPanels.map(panel => {
            const { id, payload } = panel.getPayload();
            return { ...payload, id };
        });

        const creates$: Observable<Container[]> = createRequests$.length > 0
            ? forkJoin(createRequests$)
            : of([]);

        const patch$: Observable<Container[]> = bulkPatchPayload.length > 0
            ? this.containerService.patchContainers(bulkPatchPayload)
            : of([]);

        forkJoin([creates$, patch$]).pipe(
            switchMap(([createdContainers, patchedContainers]) => {
                const updatedContainers: Container[] = [...createdContainers, ...patchedContainers];

                const customFieldRequests = this.panels.map(panel => {
                    const { customFieldValues } = panel.getPayload();
                    return this.containerCustomFieldService
                        .updateCustomFieldValues(customFieldValues)
                        .pipe(
                            defaultIfEmpty(null),
                            catchError(err => {
                                console.error('customField update failed', customFieldValues, err);
                                return of(null); // don't let one failure kill the others
                            })
                        );
                });

                return forkJoin(customFieldRequests).pipe(
                    map(() => updatedContainers)
                );
            })
        ).subscribe({
            next: updatedContainers => {
                this.containers = updatedContainers.map(updated => {
                    const previous = this.containers.find(c => c.id === updated.id);
                    return Object.assign(new Container(), updated, {
                        customValues: previous?.customValues ?? []
                    });
                });
                this.newContainerIds.clear();
                this.saving = false;
            },
            error: () => {
                this.saving = false;
            },
        });
    }
}
